/**
 * DataShield Governance Portal - API Service
 * 
 * Centralized API client with authentication, error handling,
 * request/response interceptors, and retry logic.
 * 
 * @module services/api
 */

import type { ApiResponse, ApiError, PaginatedResponse } from '@/types';

// =============================================================================
// CONFIGURATION
// =============================================================================

const API_BASE_URL = '/api';
const AUTH_TOKEN_KEY = 'datashield_auth_token';
const REFRESH_TOKEN_KEY = 'datashield_refresh_token';

export interface RequestConfig extends Omit<RequestInit, 'body'> {
  params?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  skipAuth?: boolean;
  retries?: number;
  timeout?: number;
}

// =============================================================================
// TOKEN MANAGEMENT
// =============================================================================

class TokenManager {
  private static instance: TokenManager;
  private refreshPromise: Promise<string> | null = null;

  private constructor() {}

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  getAccessToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  setAccessToken(token: string): void {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  clearTokens(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  async refreshAccessToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = fetch(`${API_BASE_URL}/sessions/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Token refresh failed');
        }
        const data = await response.json();
        this.setAccessToken(data.accessToken);
        if (data.refreshToken) {
          this.setRefreshToken(data.refreshToken);
        }
        return data.accessToken;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }
}

const tokenManager = TokenManager.getInstance();

// =============================================================================
// ERROR HANDLING
// =============================================================================

export class ApiRequestError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;
  public readonly requestId?: string;

  constructor(error: ApiError, status: number) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.code = error.code;
    this.status = status;
    this.details = error.details;
    this.requestId = error.requestId;
  }

  isAuthError(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }

  isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }

  isServerError(): boolean {
    return this.status >= 500;
  }
}

// =============================================================================
// REQUEST UTILITIES
// =============================================================================

/** Generate a unique request ID for tracing */
function generateRequestId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;
}

/** Get or create session ID for request correlation */
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('datashield_session_id');
  if (!sessionId) {
    sessionId = generateRequestId();
    sessionStorage.setItem('datashield_session_id', sessionId);
  }
  return sessionId;
}

function buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin);
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }
  
  return url.toString();
}

function buildHeaders(config: RequestConfig): Headers {
  const headers = new Headers(config.headers);
  
  if (!headers.has('Content-Type') && config.body) {
    headers.set('Content-Type', 'application/json');
  }
  
  if (!config.skipAuth) {
    const token = tokenManager.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  
  // Add request tracing headers
  headers.set('X-Request-ID', generateRequestId());
  headers.set('X-Session-ID', getSessionId());
  
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('Content-Type');
  
  if (!response.ok) {
    let error: ApiError;
    
    if (contentType?.includes('application/json')) {
      error = await response.json();
    } else {
      error = {
        code: 'UNKNOWN_ERROR',
        message: response.statusText || 'An unknown error occurred',
        timestamp: new Date().toISOString(),
        requestId: response.headers.get('X-Request-Id') || 'unknown',
      };
    }
    
    throw new ApiRequestError(error, response.status);
  }
  
  if (response.status === 204) {
    return undefined as T;
  }
  
  if (contentType?.includes('application/json')) {
    return response.json();
  }
  
  return response.text() as unknown as T;
}

// =============================================================================
// MAIN API CLIENT
// =============================================================================

async function request<T>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<T> {
  const { params, body, skipAuth, retries = 1, timeout = 30000, ...init } = config;
  
  const url = buildUrl(endpoint, params);
  const headers = buildHeaders(config);
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  const fetchConfig: RequestInit = {
    ...init,
    headers,
    signal: controller.signal,
    body: body ? JSON.stringify(body) : undefined,
  };
  
  try {
    let response = await fetch(url, fetchConfig);
    
    // Handle 401 - attempt token refresh
    if (response.status === 401 && !skipAuth) {
      try {
        await tokenManager.refreshAccessToken();
        // Retry with new token
        const newHeaders = buildHeaders(config);
        response = await fetch(url, { ...fetchConfig, headers: newHeaders });
      } catch {
        // Refresh failed, redirect to login
        tokenManager.clearTokens();
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
    }
    
    return await handleResponse<T>(response);
  } catch (error) {
    // Handle network errors and retries
    if (error instanceof ApiRequestError) {
      throw error;
    }
    
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return request<T>(endpoint, { ...config, retries: retries - 1 });
    }
    
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// =============================================================================
// HTTP METHOD HELPERS
// =============================================================================

/**
 * GET request
 */
export function get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
  return request<T>(endpoint, { ...config, method: 'GET' });
}

/**
 * POST request
 */
export function post<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
  return request<T>(endpoint, { ...config, method: 'POST', body });
}

/**
 * PUT request
 */
export function put<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
  return request<T>(endpoint, { ...config, method: 'PUT', body });
}

/**
 * PATCH request
 */
export function patch<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
  return request<T>(endpoint, { ...config, method: 'PATCH', body });
}

/**
 * DELETE request
 */
export function del<T>(endpoint: string, config?: RequestConfig): Promise<T> {
  return request<T>(endpoint, { ...config, method: 'DELETE' });
}

// =============================================================================
// CONVENIENCE METHODS
// =============================================================================

/**
 * Fetch paginated data
 */
export async function paginate<T>(
  endpoint: string,
  page = 1,
  pageSize = 20,
  filters?: Record<string, string | number | boolean | undefined>
): Promise<PaginatedResponse<T>> {
  return get<PaginatedResponse<T>>(endpoint, {
    params: { page, pageSize, ...filters },
  });
}

/**
 * Upload file with progress tracking
 */
export function uploadFile(
  endpoint: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ApiResponse<{ id: string; url: string }>> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress((event.loaded / event.total) * 100);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.response));
      } else {
        reject(new ApiRequestError(JSON.parse(xhr.response), xhr.status));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });
    
    const token = tokenManager.getAccessToken();
    xhr.open('POST', buildUrl(endpoint));
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(formData);
  });
}

/**
 * Download file
 */
export async function downloadFile(endpoint: string, filename: string): Promise<void> {
  const response = await fetch(buildUrl(endpoint), {
    headers: {
      Authorization: `Bearer ${tokenManager.getAccessToken()}`,
    },
  });
  
  if (!response.ok) {
    throw new Error('Download failed');
  }
  
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =============================================================================
// AUTH HELPERS
// =============================================================================

export const auth = {
  login: async (email: string, password: string) => {
    const response = await post<{
      accessToken: string;
      refreshToken: string;
      user: unknown;
    }>('/sessions', { email, password }, { skipAuth: true });
    
    tokenManager.setAccessToken(response.accessToken);
    tokenManager.setRefreshToken(response.refreshToken);
    
    return response;
  },
  
  logout: async () => {
    try {
      await del('/sessions/current');
    } finally {
      tokenManager.clearTokens();
      window.location.href = '/login';
    }
  },
  
  isAuthenticated: () => {
    return !!tokenManager.getAccessToken();
  },
  
  getToken: () => tokenManager.getAccessToken(),
};

// =============================================================================
// EXPORT API OBJECT
// =============================================================================

export const api = {
  get,
  post,
  put,
  patch,
  del,
  paginate,
  uploadFile,
  downloadFile,
  auth,
};

export default api;
